'use client';

import { useCallback, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import useTabStore, { setCheatsheetView } from '@/utils/useTabStore';

export interface CheatsheetViewSelectorProps {
  className?: string;
}

export const VIEW_PARAM = 'view';

type VIEW = 'grid' | 'list';

const viewDescriptors: Record<VIEW, string> = {
  grid: 'Grid',
  list: 'List',
};

const views = Object.keys(viewDescriptors) as VIEW[];

const isValidView = (view: string): view is VIEW => view in viewDescriptors;

const CheatsheetViewSelector = (props: CheatsheetViewSelectorProps) => {
  const searchParams = useSearchParams();
  const viewInUrl = searchParams.get(VIEW_PARAM);
  const view = useTabStore((state) => state.cheatsheetView);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (viewInUrl && isValidView(viewInUrl)) {
      setCheatsheetView(viewInUrl as VIEW);
    } else {
      setCheatsheetView(views[0]);
    }
  }, [viewInUrl]);

  const handleTabChange = useCallback(
    (view: string) => {
      if (!isValidView(view)) {
        return;
      }
      setCheatsheetView(view as VIEW);
      const params = new URLSearchParams(searchParams);
      params.set(VIEW_PARAM, view);
      const href = `${pathname}?${params.toString()}`;
      router.replace(href);
    },
    [router, searchParams, pathname],
  );

  const renderedTabs = views.map((view) => {
    return (
      <TabsTrigger key={view} value={view}>
        {viewDescriptors[view]}
      </TabsTrigger>
    );
  });

  return (
    <Tabs
      value={view}
      onValueChange={handleTabChange}
      className={props.className}
    >
      <TabsList>{renderedTabs}</TabsList>
    </Tabs>
  );
};

export default CheatsheetViewSelector;
